import type { BulkAction, BulkItemType, ItemSummary } from "../api/types.ts";
import { uppercaseFirstCharacter } from "@sg-protect/components";

const ITEM_TYPE_ENTITY: Record<BulkItemType, string> = {
    user: "user",
    group: "group",
    site: "workspace",
    team: "workspace",
    teamsChannel: "Teams channel",
    sharedLink: "sharing link",
    oneDrive: "OneDrive",
    archivedSite: "archived site",
    userLicense: "user license",
    tenantSettings: "tenant settings",
    documentLibrary: "document library",
    permission: "permission",
    spGroup: "membership",
    file: "file",
    sharingLinkRecipient: "sharing link recipient"
};

export function getTargetEntity(items: readonly ItemSummary[], fallback: string): string {
    const entities = new Set(items.map(item => ITEM_TYPE_ENTITY[item.itemType]));

    return entities.size === 1 ? entities.values().next().value! : fallback;
}

const TRIM_PROCESSING_TIME_HEADS_UP = "Trimming could take several minutes or hours based on your scope.";

export interface ActionLabels {
    pastTense: string;
    infinitive: string;
    // PLACEHOLDER: only deleteSharingLink and trimSiteFileVersions come from the Activity Center design; confirm the rest.
    running: string;
    runningHint?: string;
    entity: string;
    isSingleton?: boolean;
    successOverride?: (label: string) => string;
    failureOverride?: (label: string) => string;
}

function dropSingularCount(subject: string): string {
    return subject.replace(/^1 /, "");
}

function toSiteAdminRoles(subject: string): string {
    return subject.replace(/permission/, "site admin role");
}

export const ACTION_LABELS: Record<BulkAction, ActionLabels> = {
    deleteSharingLink: {
        pastTense: "deleted",
        infinitive: "delete",
        running: "Removing sharing links",
        entity: "sharing link"
    },
    addGroupOwner: {
        pastTense: "added owners to",
        infinitive: "add owners to",
        running: "Adding group owners",
        entity: "group"
    },
    addGroupMember: {
        pastTense: "added members to",
        infinitive: "add members to",
        running: "Adding group members",
        entity: "group"
    },
    removeGroupOwner: {
        pastTense: "removed owners from",
        infinitive: "remove owners from",
        running: "Removing group owners",
        entity: "group"
    },
    manageGroupOwners: {
        pastTense: "managed owners of",
        infinitive: "manage owners of",
        running: "Managing group owners",
        entity: "group"
    },
    manageGroupUsers: {
        pastTense: "managed users of",
        infinitive: "manage users of",
        running: "Managing group users",
        entity: "group"
    },
    addGroupUsers: {
        pastTense: "added users to",
        infinitive: "add users to",
        running: "Adding group users",
        entity: "group"
    },
    deleteGroup: {
        pastTense: "deleted",
        infinitive: "delete",
        running: "Deleting groups",
        entity: "group"
    },
    removeGroupGuest: {
        pastTense: "removed guests from",
        infinitive: "remove guests from",
        running: "Removing group guests",
        entity: "group"
    },
    updateGroupVisibility: {
        pastTense: "updated visibility of",
        infinitive: "update visibility of",
        running: "Updating group visibility",
        entity: "group"
    },
    deleteSite: {
        pastTense: "deleted",
        infinitive: "delete",
        running: "Deleting workspaces",
        entity: "workspace"
    },
    archiveSite: {
        pastTense: "archived",
        infinitive: "archive",
        running: "Archiving workspaces",
        entity: "workspace"
    },
    deleteArchivedSite: {
        pastTense: "deleted",
        infinitive: "delete",
        running: "Deleting archived sites",
        entity: "archived site"
    },
    updateSiteSharingCapability: {
        pastTense: "updated external sharing of",
        infinitive: "update external sharing of",
        running: "Updating external sharing",
        entity: "workspace"
    },
    unassignUserLicense: {
        pastTense: "unassigned",
        infinitive: "unassign",
        running: "Unassigning user licenses",
        entity: "user license"
    },
    removeEveryoneExceptExternalUsersPermission: {
        pastTense: "removed EEEU permission from",
        infinitive: "remove EEEU permission from",
        running: "Removing EEEU permissions",
        entity: "workspace",
        successOverride: label => `EEEU permissions have been successfully removed of ${label}`
    },
    trimSiteFileVersions: {
        pastTense: "trimmed file versions of",
        infinitive: "trim file versions of",
        running: "Trimming version history",
        runningHint: TRIM_PROCESSING_TIME_HEADS_UP,
        entity: "workspace",
        successOverride: label => `File versions trimmed for ${label}`
    },
    updateSiteFileVersionPolicy: {
        pastTense: "updated file version policy of",
        infinitive: "update file version policy of",
        running: "Updating file version policy",
        entity: "workspace",
        successOverride: label => `File version policy updated for ${label}`
    },
    updateTenantFileVersionPolicy: {
        pastTense: "updated file version policy of",
        infinitive: "update file version policy of",
        running: "Updating the tenant file version policy",
        entity: "tenant",
        isSingleton: true,
        successOverride: label => `File version policy updated for ${label}`
    },
    updateDocumentLibraryFileVersionPolicy: {
        pastTense: "updated file version policy of",
        infinitive: "update file version policy of",
        running: "Updating library file version policy",
        entity: "document library",
        successOverride: label => `File version policy updated for ${label}`
    },
    trimDocumentLibraryFileVersions: {
        pastTense: "trimmed file versions of",
        infinitive: "trim file versions of",
        running: "Trimming library version history",
        runningHint: TRIM_PROCESSING_TIME_HEADS_UP,
        entity: "document library",
        successOverride: label => `File versions trimmed for ${label}`
    },
    removeGroupMember: {
        pastTense: "removed members from",
        infinitive: "remove members from",
        running: "Removing group members",
        entity: "group"
    },
    removeDirectPermission: {
        pastTense: "removed",
        infinitive: "remove",
        running: "Removing permissions",
        entity: "permission",
        successOverride: label => `${uppercaseFirstCharacter(dropSingularCount(label))} removed`,
        failureOverride: label => `Couldn't remove ${dropSingularCount(label)}`
    },
    removePrincipalFromSPGroup: {
        pastTense: "removed",
        infinitive: "remove",
        running: "Removing SharePoint group members",
        entity: "group",
        successOverride: label => `${uppercaseFirstCharacter(dropSingularCount(label))} removed`,
        failureOverride: label => `Couldn't remove ${dropSingularCount(label)}`
    },
    removeSiteCollectionAdmin: {
        pastTense: "removed",
        infinitive: "remove",
        running: "Removing site admin roles",
        entity: "site admin role",
        successOverride: label => `${uppercaseFirstCharacter(dropSingularCount(toSiteAdminRoles(label)))} removed`,
        failureOverride: label => `Couldn't remove ${dropSingularCount(toSiteAdminRoles(label))}`
    },
    archiveFile: {
        pastTense: "archived",
        infinitive: "archive",
        running: "Archiving files",
        entity: "file"
    },
    deleteFile: {
        pastTense: "deleted",
        infinitive: "delete",
        running: "Deleting files",
        entity: "file"
    },
    removeSharingLinkRecipient: {
        pastTense: "removed",
        infinitive: "remove",
        running: "Removing sharing link recipients",
        entity: "sharing link recipient",
        successOverride: label => `${uppercaseFirstCharacter(dropSingularCount(label))} removed`,
        failureOverride: label => `Couldn't remove ${dropSingularCount(label)}`
    }
};
